import React from "react";
import {Drawer, IconButton} from "@mui/material";
import {ArrowForward, AutoAwesomeOutlined, Close, LockOutlined} from "@mui/icons-material";
import type {ContractChild, ContractParent} from "../contractCoverageData";
import {COVERAGE_CURRENCY} from "../contractCoverageData";
import {
    formatCompactCurrency,
    formatNumber,
    POSITION_LABEL,
    positionColors,
} from "../contractCoverageUtils";
import {buildExecutionPlan, PLAN_INTENTIONS} from "../executionPlan";
import type {PlanIntention} from "../executionPlan";

type Props = {
    open: boolean;
    onClose: () => void;
    parent: ContractParent;
    children: ContractChild[];
};

// Inputs we'll layer onto the plan in a later step (placeholder so the layout anticipates them).
const FUTURE_INPUTS = [
    'Category & sourcing strategy',
    'Incumbent terms & pricing',
    'Stakeholders & contract owners',
    'Risk, compliance & exit terms',
    'Target timeline & milestones',
];

export const ExecutionPlanDrawer: React.FC<Props> = ({open, onClose, parent, children}) => {
    const [intention, setIntention] = React.useState<PlanIntention | null>(null);
    const [plan, setPlan] = React.useState<ReturnType<typeof buildExecutionPlan> | null>(null);

    // Reset whenever the selection changes (new set of children).
    const [prevChildren, setPrevChildren] = React.useState(children);
    if (prevChildren !== children) {
        setPrevChildren(children);
        setPlan(null);
    }

    const generate = () => {
        if (intention) setPlan(buildExecutionPlan(parent, children, intention));
    };

    const money = (v: number) => formatCompactCurrency(v, COVERAGE_CURRENCY);

    return (
        // The app header is pinned at theme.zIndex.drawer + 1, so lift above it
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{className: 'cci-plan-paper'}}
                sx={theme => ({zIndex: theme.zIndex.drawer + 2})}>
            <div className="cci-plan">
                <header className="cci-plan-head">
                    <div>
                        <span className="cci-plan-eyebrow">Execution plan</span>
                        <h3>{parent.name}</h3>
                        <span className="cci-plan-sub">
                            {children.length} supplier{children.length === 1 ? '' : 's'} in scope
                        </span>
                    </div>
                    <IconButton onClick={onClose} size="small"><Close/></IconButton>
                </header>

                <div className="cci-plan-body">
                    {/* Scope */}
                    <section className="cci-plan-section">
                        <span className="cci-plan-label">Suppliers in scope</span>
                        <div className="cci-plan-chips">
                            {children.map((c, i) => {
                                const col = positionColors(c.position);
                                return (
                                    <span className="cci-plan-supplier" key={i}>
                                        <span className="cci-plan-supplier-name">{c.supplier}</span>
                                        {c.country && <span className="cci-country-chip cci-inline">{c.country}</span>}
                                        <span className="cci-position-pill" style={{backgroundColor: col.bg, color: col.fg}}>
                                            {POSITION_LABEL[c.position]}
                                        </span>
                                    </span>
                                );
                            })}
                        </div>
                    </section>

                    {/* Intention */}
                    <section className="cci-plan-section">
                        <span className="cci-plan-label">What do you intend to do with these suppliers?</span>
                        <div className="cci-intentions">
                            {PLAN_INTENTIONS.map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`cci-intention${intention === opt.value ? ' is-selected' : ''}`}
                                    onClick={() => setIntention(opt.value)}>
                                    <span className="cci-intention-label">{opt.label}</span>
                                    <span className="cci-intention-desc">{opt.description}</span>
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="cci-plan-generate"
                            disabled={!intention}
                            onClick={generate}>
                            <AutoAwesomeOutlined fontSize="small"/>
                            Generate plan
                            <ArrowForward fontSize="small"/>
                        </button>
                    </section>

                    {/* Generated plan */}
                    {plan && (
                        <div className="cci-plan-report">
                            <div className="cci-plan-metrics">
                                <Metric label="Cumulative spend" value={money(plan.scope.spend)}/>
                                <Metric label="Cost saving" value={plan.scope.saving > 0 ? money(plan.scope.saving) : '—'} tone="saving"/>
                                <Metric label="Cost avoidance" value={plan.scope.avoidance > 0 ? money(plan.scope.avoidance) : '—'} tone="avoidance"/>
                                <Metric label="Transactions" value={formatNumber(plan.scope.transactions)}/>
                                <Metric label="Legal entities" value={formatNumber(plan.scope.entities)}/>
                                <Metric label="Contracted" value={`${plan.scope.contracted} / ${plan.scope.count}`}/>
                            </div>

                            <section className="cci-plan-section">
                                <span className="cci-plan-label">Suggested next steps · {plan.intentionLabel}</span>
                                <ol className="cci-plan-steps">
                                    {plan.steps.map((step, i) => <li key={i}>{step}</li>)}
                                </ol>
                            </section>

                            <section className="cci-plan-future">
                                <span className="cci-plan-future-title">
                                    <LockOutlined fontSize="small"/> Inputs to add next
                                </span>
                                <p>These will enrich the plan in a following step.</p>
                                <ul>
                                    {FUTURE_INPUTS.map((f, i) => <li key={i}>{f}</li>)}
                                </ul>
                            </section>
                        </div>
                    )}
                </div>
            </div>
        </Drawer>
    );
};

const Metric: React.FC<{label: string; value: string; tone?: 'saving' | 'avoidance'}> = ({label, value, tone}) => (
    <div className="cci-plan-metric">
        <span className={`cci-plan-metric-value${tone ? ` is-${tone}` : ''}`}>{value}</span>
        <span className="cci-plan-metric-label">{label}</span>
    </div>
);
